import { useEffect, useState } from "react";
import useAuth from "../../../hook/useAuth";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import EmailValidatedToCheckExistComponent from "../../../components/EmailValidatedToCheckExistComponent";
import PasswordValidation from "../../../components/PasswordValidation";
import { PhoneNumberInput } from "../../../components/PhoneNumberInput";
import { PleasewaitPopup } from "../../../components/PleasewaitPopup";
import UsernameValidatedComponent from "../../../components/UsernameValidatedComponent";
import { baseURL } from "../../../config/api";
import { DoesEmailExist, DoesUserNameExist } from "../../../services/Users";
import useConfirm from "../../../hook/useConfirm";
import useDelete from "../../../hook/useDelete";

export const EditUser = () => {
    const navigate = useNavigate();
    const idNumber = useLocation();
    const { auth }: any = useAuth();
    const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
    const { setDeletePopup, setData: setDeleteData }: any = useDelete();
    const [firstName, setFirstName] = useState<string>("");
    const [lastName, setLastName] = useState<string>("");
    const [userName, setUserName] = useState<string>("");
    const [emailAddress, setEmailAddress] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [mobilPhoneNumber, setMobilePhoneNumber] = useState<string>("");
    const [usernameTouched, setUsernameTouched] = useState<boolean>(false);
    const [emailTouched, setEmailTouched] = useState<boolean>(false);
    const [isValidPassword, setIsValidPassword] = useState<boolean>(false);
    const [enabled, setEnabled] = useState<string>("Yes");
    const [dateFormat, setDateFormat] = useState<string>("MM/dd/yyyy KK:mm a");
    const [roleId, setRoleId] = useState<string>("");
    const [roleOptions, setRoleOptions] = useState([]);
    const [passwordTouched, setPasswordTouched] = useState<boolean>(false);
    const [pleaseWaitPopup, setPleaseWaitPopup] = useState<boolean>(false);
    const [userData, setUserData] = useState<any>(null);

    useEffect(() => {
        axios.get(`${baseURL}roles`)
            .then((response) => {
                setRoleOptions(response?.data?.map((item: any) => {
                    return { value: item.id, label: item.roleName }
                }))
            }).catch(error => {
                setRoleId("0");
                console.error(error);
            });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        setPleaseWaitPopup(true);
        axios
            .get(`${baseURL}users/${idNumber?.state}`)
            .then((response) => {
                setUserData(response?.data);
                setFirstName(response?.data?.firstName);
                setLastName(response?.data?.lastName);
                setUserName(response?.data?.userName);
                setEmailAddress(response?.data?.emailAddress);
                setDateFormat(response?.data?.dateFormat);
                setMobilePhoneNumber(response?.data?.mobilPhoneNumber);
                setEnabled(response?.data?.isActive ? "Yes" : "No");
                setRoleId(response?.data?.roleId);
                setPleaseWaitPopup(false);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idNumber]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setPleaseWaitPopup(true);

        await axios
            .put(
                `${baseURL}users?userId=${auth?.id}`,
                JSON.stringify({
                    ...userData,
                    firstName,
                    lastName,
                    userName,
                    password,
                    mobilPhoneNumber,
                    emailAddress,
                    profilePic: "",
                    isActive: enabled === "Yes" ? true : false,
                    roleId,
                    dateFormat,
                    updatedAt: new Date().toLocaleString(),
                    updatedBy: auth?.givenname

                }),
                {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: false,
                }
            )
            .then((response) => {
                setPleaseWaitPopup(false);
                navigate(-1);
            })
            .catch((error) => {
                setPleaseWaitPopup(false);
                console.error("Error:", error);
            });
    }

    const deletingUser = async (id: number) => {
        setDeleteData({
            id: id,
            title: `Delete User`,
            summary: `Are you sure you want to delete this user?`,
            isDelete: (callback: any) => {
                setPleaseWaitPopup(true);
                callback();

                axios
                    .delete(`${baseURL}users?id=${id}&userId=${auth?.id}`)
                    .then(() => {
                        setPleaseWaitPopup(false);
                        navigate(-1);
                    })
                    .catch((error) => {
                        setConfirmData({
                            title: "Warning",
                            summary: error.response?.data || "Delete failed.",
                            confirmBtn: "Ok",
                            isConfirm: (cb: any) => cb(),
                        });

                        setConfirmPopup(true);
                        setPleaseWaitPopup(false);
                    });
            },
        });

        setDeletePopup(true);
    };

    return (
        <>
            <div className={`px-[24px] py-[20px] h-[calc(100vh-142px)] overflow-auto`}>
                <div className="w-full bg-white p-4 rounded-md">
                    <h2 className={`text-lg font-semibold`}>Edit User</h2>

                    <div className="mt-2 p-2">
                        <form onSubmit={(e) => handleSubmit(e)}>
                            <div className="w-full">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-[#667085]">First Name</label>
                                            <input
                                                type="text"
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={firstName}
                                                placeholder="First Name"
                                                onChange={(e) => setFirstName(e.target.value)}
                                                required
                                            />
                                        </div>

                                        <EmailValidatedToCheckExistComponent
                                            email={emailAddress}
                                            setEmail={setEmailAddress}
                                            getEmail={DoesEmailExist}
                                            touched={emailTouched}
                                            setTouched={setEmailTouched}
                                            className={{
                                                parentDiv: "",
                                                label: "block text-xs font-medium text-[#667085]",
                                                input:
                                                    "w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent",
                                            }}
                                        />

                                        <UsernameValidatedComponent
                                            userName={userName}
                                            setUserName={setUserName}
                                            getUsername={DoesUserNameExist}
                                            touched={usernameTouched}
                                            setTouched={setUsernameTouched}
                                            className={{
                                                parentDiv: "",
                                                label: "block text-xs font-medium text-[#667085]",
                                                input:
                                                    "w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent",
                                            }}
                                        />

                                        <div>
                                            <label className="block text-xs font-medium text-[#667085]">Date Format</label>
                                            <select
                                                className={`w-full px-[8px] py-[8px] border border-secondary rounded-md outline-none focus:border-primary text-sm bg-transparent`}
                                                value={dateFormat}
                                                onChange={(e) => setDateFormat(e.target.value)}
                                            >
                                                <option value="MM/dd/yyyy KK:mm a">Use System Default</option>

                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-[#667085]">Active</label>
                                            <select
                                                className={`w-full px-[8px] py-[8px] border border-secondary rounded-md outline-none focus:border-primary text-sm bg-transparent`}
                                                value={enabled}
                                                onChange={(e) => setEnabled(e.target.value)}
                                            >
                                                <option value="Yes">Yes</option>
                                                <option value="No">No</option>

                                            </select>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-medium text-[#667085]">Last Name</label>
                                            <input
                                                type="text"
                                                className="w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent"
                                                value={lastName}
                                                placeholder="Last Name"
                                                onChange={(e) => setLastName(e.target.value)}
                                                required
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-[#667085]">Mobile Number</label>
                                            <PhoneNumberInput
                                                name={"phoneNumber"}
                                                value={mobilPhoneNumber}
                                                setValue={setMobilePhoneNumber}
                                                fontSize={"14px"}
                                                required={true}
                                            />
                                        </div>


                                        <PasswordValidation
                                            password={password}
                                            setPassword={setPassword}
                                            isValid={isValidPassword}
                                            setIsValid={setIsValidPassword}
                                            touched={passwordTouched}
                                            setTouched={setPasswordTouched}
                                            className={{
                                                parentDiv: "relative",
                                                label: "block text-xs font-medium text-[#667085]",
                                                input:
                                                    "w-full px-[8px] py-[8px] border rounded-md border-secondary outline-none focus:border-primary text-sm text-secondary bg-transparent",
                                            }}
                                        />

                                        <div>
                                            <label className="block text-xs font-medium text-[#667085]">Role</label>
                                            <select
                                                className={`w-full px-[8px] py-[8px] border border-secondary rounded-md outline-none focus:border-primary text-sm bg-transparent`}
                                                value={roleId}
                                                onChange={(e) => setRoleId(e.target.value)}
                                            >
                                                {roleOptions?.map((role: any) => (
                                                    <option key={role.value} value={role.value}>
                                                        {role.label}
                                                    </option>
                                                ))}

                                            </select>
                                        </div>

                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                                    <button
                                        type="submit"
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-primary text-white cursor-pointer"
                                    >
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate(-1)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-[#FFFFFF] text-secondary cursor-pointer border border-primary"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => deletingUser(userData?.id)}
                                        className="w-full sm:w-auto px-4 py-2 rounded bg-red-500 text-white cursor-pointer border border-red-600"
                                    >
                                        Delete
                                    </button>

                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <PleasewaitPopup showPopup={pleaseWaitPopup} />
        </>
    )
}